import { useEffect, useRef, useState } from 'react';
import {
  DRAW_LABEL, MAX_MESSAGE, MAX_BATCH, FREEHAND, parseMessage, createStore, strokesOf,
  upsertStroke, eraseIds, clearTarget, mergeSync, syncMessages, newId
} from './drawCore';

/**
 * The drawing's plumbing: one RTCDataChannel per pair, beside the media.
 *
 * WHY A DATA CHANNEL, AND WHY THIS ONE. The call is already a full mesh of
 * peer connections (pages/SessionPage.js), so every pair already has a
 * direct, encrypted path between their browsers. A stroke sent over the
 * long-poll signalling would cost a round trip through the server for every
 * thirty milliseconds of somebody's pen - and it would put drawings into a
 * table that is there for SDP. So each pair gets a channel named
 * 'jarvis-draw', ordered and reliable, and every page sends its own strokes
 * straight to every other page. Nobody relays for anybody: in a mesh there is
 * no one to relay through.
 *
 * CREATED BY THE OFFERER, BEFORE THE OFFER. A channel created before
 * createOffer is in the offer's SDP (an m=application line beside the audio
 * and the two video slots), so it comes up with the connection and never
 * needs a renegotiation - the same rule the video slots live by. The
 * answering side receives it through ondatachannel. A channel added later
 * would be a second negotiation, which is where glare lives.
 *
 * A LATE JOINER, OR A PAGE THAT RELOADED, ASKS. When a channel opens, each
 * end sends 'sync-req' and the other answers with what it holds - the board,
 * the marks on any shared screen, and whether the board is open. Everybody
 * answers, and the answers merge by stroke id, so whichever arrives first
 * fills the board and the rest change nothing.
 *
 * THE AUTHOR IS THE CHANNEL, NOT THE MESSAGE. A peer says who drew a stroke
 * by which connection it arrived on; an `author` field in the message is
 * overwritten. "Clear mine" from Marco can only ever clear Marco's.
 *
 * NOTHING HERE RENDERS. The engine keeps the strokes and tells subscribers -
 * the board and each annotation layer - that a surface changed; they redraw
 * their own canvases. The session page renders only when the board opens or
 * closes, never because somebody's pen moved.
 */

const FLUSH_MS = 30;
const LASER_MS = 900;
const CURSOR_MS = 1600;

export class DrawEngine {
  constructor() {
    this.store = createStore();
    this.channels = new Map();
    this.subs = new Map();
    this.me = null;
    this.nameOf = () => null;
    this.board = { live: false, shown: false, hidden: false, unseen: false };
    this.onBoard = null;
    this.n = 0;
    this.pending = new Map();
    this.flushTimer = null;
    this.mineByTarget = new Map();
    // Ephemera: the laser's trail, and who is drawing where. Not kept.
    this.lasers = new Map();
    this.cursors = new Map();
    this.anim = null;
    this.lastLaser = 0;
    this.received = 0;
  }

  /* ------------------------------------------------------------ channels */

  /** The offering side: the channel goes into the offer. */
  attachOffer(pc, peerId) {
    if (!pc || typeof pc.createDataChannel !== 'function') return null;
    try {
      const ch = pc.createDataChannel(DRAW_LABEL, { ordered: true });
      this.wire(ch, peerId);
      return ch;
    } catch { return null; }
  }

  /** The answering side: the channel arrives with the offer. */
  attachAnswer(pc, peerId) {
    if (!pc) return;
    pc.ondatachannel = (event) => {
      if (event?.channel?.label === DRAW_LABEL) this.wire(event.channel, peerId);
    };
  }

  wire(ch, peerId) {
    const old = this.channels.get(peerId);
    if (old && old !== ch) { try { old.close(); } catch { /* gone */ } }
    this.channels.set(peerId, ch);
    const opened = () => {
      if (this.channels.get(peerId) !== ch) return;
      this.sendTo(peerId, { t: 'sync-req' });
    };
    ch.onopen = opened;
    ch.onmessage = (e) => {
      if (this.channels.get(peerId) !== ch) return;
      this.receive(typeof e.data === 'string' ? e.data : '', peerId);
    };
    ch.onclose = () => { if (this.channels.get(peerId) === ch) this.channels.delete(peerId); };
    if (ch.readyState === 'open') opened();
  }

  /** One person's connection went: their channel, cursor and laser with it. */
  drop(peerId) {
    const ch = this.channels.get(peerId);
    this.channels.delete(peerId);
    try { ch?.close(); } catch { /* gone */ }
    for (const map of [this.cursors, this.lasers]) {
      for (const [target, per] of map) if (per.delete(peerId)) this.notify(target);
    }
  }

  /** The call is over for me: every channel, and everything drawn. */
  reset() {
    for (const id of [...this.channels.keys()]) this.drop(id);
    this.store = createStore();
    this.pending.clear();
    this.lasers.clear();
    this.cursors.clear();
    this.mineByTarget.clear();
    this.setBoard({ live: false, shown: false, hidden: false, unseen: false });
    for (const t of this.subs.keys()) this.notify(t);
  }

  openCount() {
    let n = 0;
    for (const ch of this.channels.values()) if (ch.readyState === 'open') n += 1;
    return n;
  }

  send(msg) {
    const raw = JSON.stringify(msg);
    if (raw.length > MAX_MESSAGE) return false;
    for (const ch of this.channels.values()) {
      if (ch.readyState === 'open') { try { ch.send(raw); } catch { /* closing */ } }
    }
    return true;
  }

  sendTo(peerId, msg) {
    const ch = this.channels.get(peerId);
    const raw = JSON.stringify(msg);
    if (!ch || ch.readyState !== 'open' || raw.length > MAX_MESSAGE) return false;
    try { ch.send(raw); return true; } catch { return false; }
  }

  /* -------------------------------------------------------- subscriptions */

  subscribe(target, fn) {
    let set = this.subs.get(target);
    if (!set) { set = new Set(); this.subs.set(target, set); }
    set.add(fn);
    return () => { set.delete(fn); };
  }

  notify(target) {
    const set = this.subs.get(target);
    if (set) for (const fn of set) { try { fn(); } catch { /* a canvas gone */ } }
  }

  strokes(target) { return strokesOf(this.store, target); }

  setBoard(patch) {
    const next = { ...this.board, ...patch };
    if (Object.keys(next).every((k) => next[k] === this.board[k])) return;
    this.board = next;
    if (this.onBoard) this.onBoard(next);
  }

  /* ------------------------------------------------------------ receiving */

  receive(raw, from) {
    const m = parseMessage(raw);
    if (!m) return;
    this.received += 1;
    switch (m.t) {
      case 'stroke': {
        const s = { ...m, author: from };
        if (upsertStroke(this.store, s)) {
          const last = s.points[s.points.length - 1];
          if (last) this.cursorAt(s.target, from, last, s.color);
          if (s.target === 'board' && !this.board.shown) this.setBoard({ unseen: true });
          this.notify(s.target);
        }
        break;
      }
      case 'erase':
        for (const t of eraseIds(this.store, m.ids)) this.notify(t);
        break;
      case 'clear':
        if (clearTarget(this.store, m.target, m.scope === 'mine' ? from : null)) this.notify(m.target);
        break;
      case 'laser':
        this.laserAt(m.target, from, [m.x, m.y]);
        break;
      case 'board-open':
        this.setBoard({ live: true, shown: this.board.hidden ? this.board.shown : true });
        break;
      case 'sync-req':
        for (const target of this.store.targets.keys()) {
          if (target === 'board') continue;
          for (const msg of syncMessages(this.store, target)) this.sendTo(from, msg);
        }
        // The board always, so whether it is open arrives with it.
        for (const msg of syncMessages(this.store, 'board', this.board.live)) this.sendTo(from, msg);
        break;
      case 'sync':
        if (mergeSync(this.store, m.target, m.strokes)) this.notify(m.target);
        if (m.target === 'board' && m.open && !this.board.live) {
          this.setBoard({ live: true, shown: this.board.hidden ? this.board.shown : true });
        }
        break;
      default:
    }
  }

  /* -------------------------------------------------------- my own drawing */

  /**
   * Start a stroke of mine. Applied here at once, so my own pen never waits
   * for the network, and sent in batches every FLUSH_MS while it grows.
   */
  begin(target, { tool, color, width, point, text }) {
    this.n += 1;
    const id = newId(this.me, this.n);
    const s = { id, author: this.me, target, tool, color, width, points: [point], done: false };
    if (text !== undefined) s.text = text;
    upsertStroke(this.store, s);
    this.pending.set(id, { target, tool, color, width, text, fresh: [point], sentAny: false });
    let mine = this.mineByTarget.get(target);
    if (!mine) { mine = []; this.mineByTarget.set(target, mine); }
    mine.push(id);
    this.notify(target);
    this.schedule();
    return id;
  }

  extend(id, point) {
    const p = this.pending.get(id);
    const s = this.store.targets.get(p?.target)?.get(id);
    if (!p || !s) return;
    if (FREEHAND.has(s.tool)) {
      upsertStroke(this.store, { ...s, points: [point], done: false });
      p.fresh.push(point);
    } else {
      s.points = [s.points[0], point];
      p.fresh = s.points;
    }
    this.notify(p.target);
    this.schedule();
  }

  end(id) {
    const p = this.pending.get(id);
    if (!p) return;
    p.done = true;
    const s = this.store.targets.get(p.target)?.get(id);
    if (s) s.done = true;
    this.flush();
  }

  schedule() {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => { this.flushTimer = null; this.flush(); }, FLUSH_MS);
  }

  flush() {
    if (this.flushTimer) { clearTimeout(this.flushTimer); this.flushTimer = null; }
    for (const [id, p] of this.pending) {
      const s = this.store.targets.get(p.target)?.get(id);
      if (!s) { this.pending.delete(id); continue; }
      const free = FREEHAND.has(p.tool);
      // A freehand stroke's first message carries everything so far; after
      // that, only what is new. A shape always sends its two corners.
      const pts = free ? (p.sentAny ? p.fresh : s.points) : s.points;
      if (p.sentAny && !p.done && pts.length === 0) continue;
      const parts = [];
      if (free) for (let i = 0; i < pts.length; i += MAX_BATCH) parts.push(pts.slice(i, i + MAX_BATCH));
      else parts.push(pts);
      if (parts.length === 0) parts.push([]);
      parts.forEach((part, i) => {
        this.send({ t: 'stroke', id, target: p.target, tool: p.tool, color: p.color, width: p.width,
          points: part, done: Boolean(p.done && i === parts.length - 1),
          ...(p.text !== undefined ? { text: p.text } : {}) });
      });
      p.sentAny = true;
      p.fresh = [];
      if (p.done) this.pending.delete(id);
    }
  }

  /** My last stroke on this surface, gone (for me and everybody). */
  undo(target) {
    const mine = this.mineByTarget.get(target) || [];
    while (mine.length) {
      const id = mine.pop();
      if (this.store.targets.get(target)?.has(id)) { this.erase([id]); return true; }
    }
    return false;
  }

  erase(ids) {
    if (!ids.length) return;
    for (const t of eraseIds(this.store, ids)) this.notify(t);
    for (let i = 0; i < ids.length; i += 400) this.send({ t: 'erase', ids: ids.slice(i, i + 400) });
  }

  clear(target, scope) {
    if (clearTarget(this.store, target, scope === 'mine' ? this.me : null)) this.notify(target);
    this.send({ t: 'clear', target, scope });
  }

  /** Forget a surface here only - a share that stopped. Everybody does this for themselves. */
  forget(target) {
    if (clearTarget(this.store, target)) this.notify(target);
    this.lasers.delete(target);
    this.cursors.delete(target);
    this.mineByTarget.delete(target);
  }

  hasStrokes(target) { return (this.store.targets.get(target)?.size || 0) > 0; }

  /* ------------------------------------------------------------ ephemera */

  laser(target, point) {
    this.laserAt(target, this.me, point);
    const now = Date.now();
    if (now - this.lastLaser < FLUSH_MS) return;
    this.lastLaser = now;
    this.send({ t: 'laser', target, x: point[0], y: point[1] });
  }

  laserAt(target, who, point) {
    let per = this.lasers.get(target);
    if (!per) { per = new Map(); this.lasers.set(target, per); }
    const trail = per.get(who) || [];
    trail.push({ x: point[0], y: point[1], at: Date.now() });
    if (trail.length > 40) trail.splice(0, trail.length - 40);
    per.set(who, trail);
    this.notify(target);
    this.animate();
  }

  cursorAt(target, who, point, color) {
    let per = this.cursors.get(target);
    if (!per) { per = new Map(); this.cursors.set(target, per); }
    per.set(who, { x: point[0], y: point[1], at: Date.now(), color, name: this.nameOf(who) || 'Somebody' });
    this.animate();
  }

  /** The laser trails still glowing, and who is drawing, for one surface. */
  ephemera(target) {
    const now = Date.now();
    const lasers = [];
    for (const [who, trail] of this.lasers.get(target) || []) {
      const live = trail.filter((p) => now - p.at < LASER_MS);
      if (live.length) lasers.push({ who, points: live.map((p) => ({ ...p, age: (now - p.at) / LASER_MS })) });
    }
    const cursors = [];
    for (const [who, c] of this.cursors.get(target) || []) {
      if (now - c.at < CURSOR_MS) cursors.push({ who, ...c, age: (now - c.at) / CURSOR_MS });
    }
    return { lasers, cursors };
  }

  /** Repaint while anything is fading, and stop when nothing is. */
  animate() {
    if (this.anim || typeof window === 'undefined') return;
    const tick = () => {
      this.anim = null;
      const now = Date.now();
      let more = false;
      const touched = new Set();
      for (const [target, per] of this.lasers) {
        for (const [who, trail] of per) {
          const live = trail.filter((p) => now - p.at < LASER_MS);
          if (live.length !== trail.length) touched.add(target);
          if (live.length) { per.set(who, live); more = true; touched.add(target); } else per.delete(who);
        }
      }
      for (const [target, per] of this.cursors) {
        for (const [who, c] of per) {
          if (now - c.at < CURSOR_MS) { more = true; touched.add(target); } else { per.delete(who); touched.add(target); }
        }
      }
      for (const t of touched) this.notify(t);
      if (more) this.anim = window.requestAnimationFrame(tick);
    };
    this.anim = window.requestAnimationFrame(tick);
  }
}

/**
 * The engine, once per page, and the board's open/closed state as React
 * state - the one part of drawing the session page renders from.
 */
export function useDraw(me, nameOf) {
  const ref = useRef(null);
  if (!ref.current) ref.current = new DrawEngine();
  const engine = ref.current;
  const [board, setBoard] = useState(engine.board);
  engine.me = me?.id || engine.me;
  engine.nameOf = nameOf;
  useEffect(() => {
    engine.onBoard = setBoard;
    // For e2e/session-draw.mjs, which reads what arrived rather than guessing.
    if (typeof window !== 'undefined') window.__jarvisDraw = engine;
    return () => { engine.onBoard = null; };
  }, [engine]);

  const openBoard = () => {
    if (!engine.board.live) {
      engine.setBoard({ live: true, shown: true, hidden: false, unseen: false });
      engine.send({ t: 'board-open' });
    } else {
      engine.setBoard({ shown: true, hidden: false, unseen: false });
    }
  };
  const hideBoard = () => engine.setBoard({ shown: false, hidden: true });
  return { engine, board, openBoard, hideBoard };
}

/** Read a colour token as a real colour, for the canvas. */
export function cssVar(v) {
  if (!v || typeof document === 'undefined') return null;
  const got = getComputedStyle(document.documentElement).getPropertyValue(v).trim();
  return got || null;
}

export const tokenColor = (name, table) => cssVar(table?.[name]);
